// ./src/utils/dashboardCards.ts
//
// ROADMAP #190 — the event dashboard's cards: which are shown, in what order (owner, 2026-10-20: "user should be able to
// enable/disable/reorder widgets on the dashboard on event level and also account level").
//
// ⚠ A MIRROR of the API's `src/boundary/dashboardCards.ts` (the ids, the built-in order, how a list is completed):
//   the server resolves the list a dashboard draws (`eventDashboardCards` — the event's own, else its account's default,
//   else the built-in) and stores what the drawers save. The app needs the same ids to draw the cards and to name them,
//   and the built-in order for when that read fails. `npm run check-dashboard-cards` (eventsmagic-api) runs both.
// ⚠ A card that is ON is still not drawn when it has nothing to show; a HIDDEN card is not mounted, so it reads nothing.
//
// Pure — no React, no services.

/** Every card the event dashboard can show. ⚠ Ids are stored: never rename one. */
export const DASHBOARD_CARD = {
  ARRIVALS: "arrivals",
  REGISTRATIONS: "registrations",
  ADDONS: "addons",
  VOUCHERS: "vouchers",
  VOLUNTEERS: "volunteers",
  ANSWERS: "answers",
  WEATHER: "weather",
  TASKS: "tasks",
  BUDGET: "budget",
  TIMELINE: "timeline",
  SIGNUPS: "signups",
  POLLS: "polls",
} as const;

export type DashboardCardId = (typeof DASHBOARD_CARD)[keyof typeof DASHBOARD_CARD];

/** The built-in order — the dashboard's order before #190. */
export const DASHBOARD_CARD_ORDER: readonly DashboardCardId[] = [
  DASHBOARD_CARD.ARRIVALS,
  DASHBOARD_CARD.REGISTRATIONS,
  DASHBOARD_CARD.ADDONS,
  DASHBOARD_CARD.VOUCHERS,
  DASHBOARD_CARD.VOLUNTEERS,
  DASHBOARD_CARD.ANSWERS,
  DASHBOARD_CARD.WEATHER,
  DASHBOARD_CARD.TASKS,
  DASHBOARD_CARD.BUDGET,
  DASHBOARD_CARD.TIMELINE,
  DASHBOARD_CARD.SIGNUPS,
  DASHBOARD_CARD.POLLS,
];

export interface DashboardCardSetting {
  id: DashboardCardId;
  hidden: boolean;
}

/** Where a list came from (the server's `source` / `accountSource`). */
export const DASHBOARD_CARDS_SOURCE = {
  EVENT: "event",
  ACCOUNT: "account",
  DEFAULT: "default",
} as const;

export const isDashboardCardId = (id: unknown): id is DashboardCardId =>
  typeof id === "string" && (DASHBOARD_CARD_ORDER as readonly string[]).includes(id);

/** A list as the server sent it, read leniently: known ids, each once. Null when there is nothing usable. */
export const normaliseDashboardCards = (raw: unknown): DashboardCardSetting[] | null => {
  if (!Array.isArray(raw)) {
    return null;
  }

  const seen = new Set<string>();
  const out: DashboardCardSetting[] = [];

  for (const item of raw) {
    const id = (item as { id?: unknown } | null)?.id;

    if (isDashboardCardId(id) && !seen.has(id)) {
      seen.add(id);
      out.push({ id, hidden: (item as { hidden?: unknown }).hidden === true });
    }
  }

  return out.length > 0 ? out : null;
};

/** The list with every card in it: one it does not name is added, shown, right after its built-in predecessor (else last). */
export const completeDashboardCards = (cards: DashboardCardSetting[] | null | undefined): DashboardCardSetting[] => {
  const out: DashboardCardSetting[] = [...(cards ?? [])];

  DASHBOARD_CARD_ORDER.forEach((id, index) => {
    if (out.some((card) => card.id === id)) {
      return;
    }

    let at = out.length;

    for (let before = index - 1; before >= 0; before -= 1) {
      const found = out.findIndex((card) => card.id === DASHBOARD_CARD_ORDER[before]);

      if (found >= 0) {
        at = found + 1;
        break;
      }
    }

    out.splice(at, 0, { id, hidden: false });
  });

  return out;
};

/** The built-in list — every card, shown, in the built-in order. */
export const defaultDashboardCards = (): DashboardCardSetting[] => DASHBOARD_CARD_ORDER.map((id) => ({ id, hidden: false }));

/** What the event is, for which cards apply to it. Null: no event (the account default — every card applies). */
export interface DashboardCardEventFacts {
  ticketed: boolean;
}

/** Does this card exist on this event's dashboard at all? Add-ons are sold with tickets only. */
export const dashboardCardApplies = (id: DashboardCardId, facts: DashboardCardEventFacts | null): boolean => {
  if (!facts) {
    return true;
  }

  return id !== DASHBOARD_CARD.ADDONS || facts.ticketed;
};

/**
 * The whole list after the cards the drawer SHOWED were rearranged: each shown card takes the next slot a shown card held,
 * so a card the drawer left out (Add-ons on an RSVP event) keeps its place for when it applies again.
 */
export const mergeShownCards = (
  full: DashboardCardSetting[],
  shown: DashboardCardSetting[],
  applies: (id: DashboardCardId) => boolean,
): DashboardCardSetting[] => {
  const queue = [...shown];

  return full.map((card) => (applies(card.id) ? queue.shift() ?? card : card));
};

/** The ids to draw, in order (shown cards only). */
export const shownDashboardCardIds = (cards: DashboardCardSetting[]): DashboardCardId[] =>
  cards.filter((card) => !card.hidden).map((card) => card.id);

/** Two lists say the same thing (order and shown/hidden). */
export const sameDashboardCards = (a: DashboardCardSetting[] | null | undefined, b: DashboardCardSetting[] | null | undefined): boolean =>
  JSON.stringify((a ?? []).map((c) => [c.id, c.hidden])) === JSON.stringify((b ?? []).map((c) => [c.id, c.hidden]));

/** What a dashboard holds: the list for which event (null: none yet), and whether it is the failed-read fallback. */
export interface DashboardCardsState {
  eventId: string | null;
  cards: DashboardCardSetting[] | null;
  failed: boolean;
}

/**
 * The state after a read of `eventId`'s cards. A good answer is drawn (completed). ⚠ A FAILED read keeps the list already
 * drawn for THIS event (a failed Refresh must not mount the cards the host hid — #190 review); only a first read that
 * fails draws every card in the built-in order (not knowing is no reason to hide one).
 */
export const nextDashboardCardsState = (
  prev: DashboardCardsState,
  eventId: string,
  read: { ok: boolean; cards?: unknown },
): DashboardCardsState => {
  if (read.ok) {
    return { eventId, cards: completeDashboardCards(normaliseDashboardCards(read.cards)), failed: false };
  }

  if (prev.eventId === eventId && prev.cards) {
    return prev;
  }

  return { eventId, cards: defaultDashboardCards(), failed: true };
};
